/**
 * Plan catalogue for the paywall, mirroring the marketing pricing page
 * (`tessera-marketing-website` → `src/components/marketing/pricing-cards.tsx`).
 *
 * Prices here are display copy. Stripe Checkout is the source of truth for the
 * amount charged. Interval tabs map to POST /api/v1/billing/checkout `{ plan, interval }`.
 * The launch offer is shown on Monthly (banner + intro prices), not as a separate tab —
 * same as the marketing site. Checkout uses `3month` while the offer is live.
 */

import type { BillingCheckoutInterval, BillingPlan } from "../types/billing";

export type BillingInterval = "monthly" | "launch" | "yearly";

/** Visible tabs — Monthly / Yearly only (matches marketing pricing page). */
export const BILLING_INTERVAL_TABS: readonly {
  id: Exclude<BillingInterval, "launch">;
  label: string;
}[] = [
  { id: "monthly", label: "Monthly" },
  { id: "yearly", label: "Yearly" },
];

export const DEFAULT_BILLING_INTERVAL: BillingInterval = "monthly";

export function checkoutIntervalFromUi(interval: BillingInterval): BillingCheckoutInterval {
  if (interval === "yearly") return "year";
  if (interval === "launch") return "3month";
  return "month";
}

/** Monthly tab headlines intro pricing while the paywall shows the launch offer. */
export function paywallShowsIntroMonthlyPricing(
  interval: BillingInterval,
  launchOfferLive: boolean
): boolean {
  return launchOfferLive && (interval === "monthly" || interval === "launch");
}

export function checkoutIntervalForPaywallTab(
  interval: BillingInterval,
  useIntroOfferCheckout: boolean
): BillingCheckoutInterval {
  if (useIntroOfferCheckout && paywallShowsIntroMonthlyPricing(interval, true)) {
    return "3month";
  }
  return checkoutIntervalFromUi(interval);
}

/** Always Monthly / Yearly — the 3-month offer is not a separate tab. */
export function billingIntervalTabs(
  _launchOfferLive?: boolean
): readonly {
  id: Exclude<BillingInterval, "launch">;
  label: string;
}[] {
  return BILLING_INTERVAL_TABS;
}

export function defaultBillingInterval(_launchOfferLive?: boolean): BillingInterval {
  return DEFAULT_BILLING_INTERVAL;
}

export const PAYWALL_HEADER = {
  titleLead: "Choose your",
  titleAccent: "plan",
  description:
    "Start free, then pick the plan that fits how much you build, test and run.",
} as const;

export const LAUNCH_OFFER = {
  badge: "Exclusive launch offer",
  titleLead: "3 months for as little as",
  titleAccent: "$45",
  description:
    "Join Tessera at our lowest membership cost. $15 per month for Standard and $30 per month for Plus for the first 3 months. Full platform access. Limited time. After that, membership continues at the regular monthly rate.",
} as const;

export interface SubscriptionPlanPrice {
  amount: string;
  /** Omitted for `yearly`, where the card derives a per-month figure. */
  period?: string;
  launchOffer?: string;
}

export interface SubscriptionPlanDefinition {
  id: string;
  name: string;
  description: string;
  /** Right-hand outline badge on the card header. */
  usageLabel: string;
  featuresHeading: string;
  features: readonly string[];
  prices: Record<BillingInterval, SubscriptionPlanPrice>;
  cta: string;
  /** Tinted card + gradient CTA. */
  highlighted: boolean;
  checkoutPlan: BillingPlan;
}

export const SUBSCRIPTION_PLANS: readonly SubscriptionPlanDefinition[] = [
  {
    id: "standard",
    name: "Standard",
    description:
      "Build and run your first strategies: full journal and statistics, AI strategy building and one live broker slot.",
    usageLabel: "Standard usage",
    featuresHeading: "What's included:",
    features: [
      "Trade log, statistics & unlimited broker accounts",
      "Reports (10 per week, 5 simulations each)",
      "800 AI credits a month for TES",
      "300 backtests a month · 5-minute candles and up",
      "Launchpad: 1 live broker slot, Autopilot included",
      "Pine Script export & University",
    ],
    prices: {
      monthly: { amount: "$39", period: "per month" },
      yearly: { amount: "$390" },
      launch: { amount: "$39", period: "per month" },
    },
    cta: "Get started",
    highlighted: false,
    checkoutPlan: "standard",
  },
  {
    id: "plus",
    name: "Plus",
    description:
      "For active strategy builders: deeper history, 1-minute data and more strategies running at once.",
    usageLabel: "Higher usage",
    featuresHeading: "Everything in Standard, plus:",
    features: [
      "Reports (40 per week, 16 simulations each)",
      "2,100 AI credits a month · +20% on top-ups",
      "1,500 backtests a month · full history from 15m, 3 years on 5m, 1 year on 1m",
      "Launchpad: 3 live broker slots, 10 running",
      "Priority access to new features",
    ],
    prices: {
      monthly: { amount: "$99", period: "per month" },
      yearly: { amount: "$990" },
      launch: { amount: "$99", period: "per month" },
    },
    cta: "Get started",
    highlighted: true,
    checkoutPlan: "plus",
  },
  {
    id: "pro",
    name: "Pro",
    description: "For heavy users running several live strategies and large research workloads.",
    usageLabel: "Highest usage",
    featuresHeading: "Everything in Plus, plus:",
    features: [
      "Reports (100 per week, 32 simulations each)",
      "5,500 AI credits a month · +20% on top-ups",
      "5,000 backtests a month",
      "Launchpad: 8 live broker slots, 25 running",
    ],
    prices: {
      monthly: { amount: "$249", period: "per month" },
      yearly: { amount: "$2,490" },
      launch: { amount: "$249", period: "per month" },
    },
    cta: "Get started",
    highlighted: false,
    checkoutPlan: "pro",
  },
];

/** Yearly cards headline the per-month equivalent of the annual amount. */
export function monthlyFromAnnual(annualAmount: string): string {
  const annual = Number.parseFloat(annualAmount.replace(/[^\d.]/g, ""));
  if (!Number.isFinite(annual)) return annualAmount;

  const monthly = annual / 12;
  if (Number.isInteger(monthly)) return `$${monthly}`;
  return `$${(Math.round(monthly * 100) / 100).toFixed(2)}`;
}

/** 3-month offer cards headline the per-month equivalent of the 3-month total. */
export function monthlyFromThreeMonth(totalAmount: string): string {
  const total = Number.parseFloat(totalAmount.replace(/[^\d.]/g, ""));
  if (!Number.isFinite(total)) return totalAmount;

  const monthly = total / 3;
  if (Number.isInteger(monthly)) return `$${monthly}`;
  return `$${(Math.round(monthly * 100) / 100).toFixed(2)}`;
}
